import { supabase } from "../supabase/SupabaseClient";
import { traerTodas } from "./paginar";
import { createRow } from "./supabaseRealtime";
import { actual } from "./quien";

/* Vacaciones tomadas (P-172, Natalia). La tabla guarda lo TOMADO; los días que
 * corresponden por antigüedad los calcula `shared/vacaciones.js` desde la
 * nómina (que la pantalla ya trae con `traerNomina`).
 *
 * Mismas llaves que el resto de RRHH: se lee con `ver.rrhh`, se escribe con
 * `cargar.rrhh`. La pared es la RLS; sin la llave, la consulta vuelve vacía y
 * la pantalla lo distingue con el permiso que ya tiene a mano.
 *
 * ANULAR NO BORRA: la fila queda con quién y cuándo, igual que infracciones y
 * viajes. Una vacación mal cargada se anula, no desaparece.
 */

const CAMPOS =
  "id,dni,anio,desde,hasta,dias,confirmado,nota," +
  "cargado_por,creado_en,anulado_en,anulado_por";

/* Las vacaciones de un año, para la pantalla. Trae también las no confirmadas
 * (la pantalla las muestra sin tilde) y las anuladas se filtran acá: son
 * historia, no descuentan ni se listan como vigentes.
 *
 * ── POR QUÉ PAGINA Y NO PIDE UN `.limit` GRANDE ─────────────────────────────
 *
 * Tenía `.limit(2000)` y `sinLimitesFalsos.test` lo marcaba en rojo, con razón:
 * PostgREST corta en 1.000 y el límite del cliente no levanta el techo del
 * servidor. Un `.limit(2000)` se lee como «traigo todas con margen» y lo que
 * hace es traer mil y callarse — es el error del tablero de Presidencia, donde
 * mayo apareció con 75 viajes en vez de 636.
 *
 * Hoy la tabla tiene 21 filas, así que nada se está cortando. La diferencia no
 * es el número de hoy: es que acá lo que se corta son DÍAS DE VACACIONES DE
 * UNA PERSONA. Un período que no llega no se ve como un error, se ve como que
 * esa persona no se tomó nada — y el saldo que la pantalla le muestra de más
 * es exactamente el que alguien va a usar para autorizarle otra licencia.
 *
 * `traerTodas` le pregunta a la base cuántas hay (`count: "exact"`) y falla si
 * no llega a esa cantidad. El orden desempata por `id`: paginar por `desde`
 * solo —que se repite, media empresa arranca vacaciones el mismo lunes— haría
 * que una fila salga dos veces y otra no salga nunca.
 */
export function traerVacaciones(anio) {
  return traerTodas(() =>
    supabase
      .from("vacaciones_tomadas")
      .select(CAMPOS, { count: "exact" })
      .eq("anio", anio)
      .is("anulado_en", null)
      .order("desde", { ascending: false })
      .order("id", { ascending: false }),
  );
}

/* Anota un período tomado. Por `createRow`, que sella quién lo cargó. Nace SIN
 * confirmar: el casillero OK lo tilda una persona cuando la vacación se tomó
 * de verdad. */
export const crearVacacion = ({ dni, anio, desde, hasta, dias, nota }) =>
  createRow("vacaciones_tomadas", null, {
    dni: String(dni).replace(/\D/g, ""),
    anio: Number(anio),
    desde,
    hasta,
    dias: Number(dias),
    confirmado: false,
    nota: (nota || "").trim() || null,
  });

/* Tilda o destilda el OK. Es lo único que decide si el período descuenta de lo
 * que corresponde: una vacación sin confirmar está anotada pero no tomada. */
export async function confirmarVacacion(id, confirmado) {
  const { data, error } = await supabase
    .from("vacaciones_tomadas")
    .update({ confirmado: !!confirmado })
    .eq("id", id)
    .is("anulado_en", null)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/* Como en infracciones: no pasa por `updateRow` para que el anulador no pise a
 * quién la cargó. */
export async function anularVacacion(id) {
  const quien = actual();
  const { data, error } = await supabase
    .from("vacaciones_tomadas")
    .update({
      anulado_en: new Date().toISOString(),
      anulado_por: quien?.mail || null,
    })
    .eq("id", id)
    .is("anulado_en", null)
    .select()
    .single();
  if (error) throw error;
  return data;
}
